import type { TooltipContentProps, TooltipProps } from '@radix-ui/react-tooltip'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cx } from 'class-variance-authority'
import type { ReactNode } from 'react'

type ExtendedTooltipProps = TooltipProps &
  Omit<TooltipContentProps, 'content'> & {
    content?: ReactNode
    multiline?: boolean
    icon?: ReactNode
    iconAriaLabel?: string
    buttonClassName?: string
    enabled?: boolean
  }

export function Tooltip({
  children,
  content,
  open,
  defaultOpen,
  onOpenChange,
  delayDuration = 800,
  disableHoverableContent,
  multiline,
  className,
  side = 'top',
  icon,
  iconAriaLabel,
  buttonClassName,
  enabled = true,
  ...contentProps
}: ExtendedTooltipProps) {
  const IconTrigger = icon ? (
    <button
      type="button"
      className={cx(
        'focus inline-flex cursor-help items-center',
        buttonClassName,
      )}
      aria-label={iconAriaLabel || 'Information'}
      tabIndex={0}
      onClick={(e) => e.preventDefault()}
    >
      {icon}
    </button>
  ) : (
    children
  )

  if (!enabled) return IconTrigger

  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        disableHoverableContent={disableHoverableContent}
      >
        <TooltipPrimitive.Trigger asChild>
          {IconTrigger}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            className={cx(
              className,
              multiline && 'w-[191px]',
              'pointer-events-none z-50 max-w-96 rounded-xs bg-[#393939] px-4 py-3 text-sm font-normal text-white',
            )}
            side={side}
            align="center"
            {...contentProps}
          >
            {content}
            <TooltipPrimitive.Arrow width={11} height={5} className="mb-1" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}
